/**
 * Am I running inside the installed app, rather than in a browser?
 *
 * This decides whether prices exist, so getting it wrong in the permissive
 * direction is a Play policy violation — see canShowPurchase() below.
 *
 * IT USED TO ASK CAPACITOR, AND CAPACITOR NEVER ANSWERED. The old check was
 * `Capacitor.isNativePlatform()`, which reads a global the native runtime
 * injects into the page. MeMo's shell does not load a bundled page: it points
 * at the live site (capacitor.config.json → server.url). And Capacitor only
 * injects that global into content it serves itself —
 *
 *     private boolean isMainUrl(Uri loadingUrl) {
 *         return (bridge.getServerUrl() == null && …);
 *     }
 *
 * — so with server.url set, nothing is injected, window.Capacitor is undefined,
 * and the check answered "browser" on every screen of the installed app. Every
 * price, every plan, every Subscribe button was on display inside the Play
 * Store build. It read like a working feature, which is how it survived.
 *
 * So the question is now asked of the user agent, which no injection can miss:
 *
 *  1. `MeMoApp` — appended by our own shell (capacitor.config.json →
 *     appendUserAgent), applied to the WebView's settings and therefore present
 *     whatever page is loaded. Definitive, from the build that introduces it.
 *
 *  2. Capacitor's global, if it happens to be there. Costs nothing and covers a
 *     future build that stops using server.url.
 *
 *  3. The Android WebView's own `wv` token. This is the one that matters for
 *     the copies ALREADY INSTALLED, which have no MeMoApp in their user agent
 *     and cannot be reached by anything except a new bundle.
 *
 * On (3), the trade is deliberate. `wv` means "an Android WebView", not
 * "MeMo" — so somebody opening memocal.app inside Facebook's or Gmail's in-app
 * browser is also treated as the app and shown no prices. That costs a sale
 * that was unlikely anyway, and the alternative costs the app: prices visible
 * in the Play build risk removal, with users already inside. Hiding too much is
 * recoverable, being removed is not.
 */

/** True inside the installed app (and inside any Android WebView). */
export function isNativeApp() {
  try {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    // No user agent to read is not a browser we recognise, and the answer that
    // costs nothing is the cautious one. A browser reporting no UA at all loses
    // sight of the prices; that is a rounding error against the alternative.
    if (!ua) return true;
    if (/\bMeMoApp\b/.test(ua)) return true;                 // our own shell
    if (/;\s*wv[;)]/.test(ua)) return true;                  // any Android WebView
    // eslint-disable-next-line no-undef
    const cap = typeof window !== 'undefined' ? window.Capacitor : undefined;
    if (cap?.isNativePlatform?.() === true) return true;
    if (typeof cap?.getPlatform === 'function' && cap.getPlatform() !== 'web') return true;
    return false;
  } catch (_) {
    // Unreadable environment: assume the app. The safe answer is the one that
    // shows no prices — see the note above.
    return true;
  }
}

/**
 * Whether this build may show prices, plans and a way to buy.
 *
 * On the web: yes, of course — memocal.app sells its own subscriptions.
 *
 * Inside the app downloaded from the Play Store: NO. Google's payments policy
 * requires an app distributed on Play to sell digital features through Play's
 * own billing, and treats sending the user to an outside checkout — or even
 * telling them one exists — as a violation. It is not a rule that stops you
 * publishing: it is one that gets the app REMOVED later, with users already
 * inside. This is what got Fortnite thrown off the store in 2021.
 *
 * So the Android app neither sells nor points at selling. Somebody who
 * subscribed on the website simply finds their features unlocked when they sign
 * in, which is allowed and is how Netflix and Spotify work on Play.
 *
 * This is deliberately a separate, named thing rather than a bare isNativeApp()
 * check at each call site, so that the REASON travels with the decision.
 */
export function canShowPurchase() {
  return !isNativeApp();
}
